// skillgenpro-frontend/src/error-framework/globalErrorHandlers.js
//
// Audited 2026-08: the app had NO window-level error handling of any kind.
// The only safety net was <ErrorBoundary> in App.js, and a React error
// boundary is a RENDER-PHASE net only — by design it cannot see:
//   * a rejected promise (`.then()` with no `.catch()`)
//   * an async failure inside an event handler
//   * a failed dynamic `import()` (ChunkLoadError)
//   * a script/resource load error
//
// Those all surfaced as either total silence or an "Uncaught (in promise)"
// line no user would ever read. This module closes that gap.
//
// It is intentionally install-once and idempotent, registers only passive
// listeners, and never calls preventDefault on anything it does not fully
// understand — so existing behaviour is unchanged on every path that was
// already working.

import { logError, logWarn, newErrorId } from './logger';

let installed = false;
let onChunkError = null;

/**
 * A failed dynamic import is almost always a DEPLOYMENT VERSION MISMATCH:
 * the user's open tab is running build A, a new build B has replaced the
 * hashed chunk files on the CDN, and the lazily-requested chunk now 404s.
 * The only real fix is a reload onto the new build — but a silent forced
 * reload can discard unsaved work, so this surfaces a prompt instead.
 */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = (error.name || '').toString();
  const message = (error.message || error.toString() || '').toString();
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

/** Register a callback used to surface the "new version — reload" prompt. */
export const setChunkErrorHandler = (fn) => {
  onChunkError = typeof fn === 'function' ? fn : null;
};

const handleChunk = (error, source) => {
  const errorId = newErrorId('chunk');
  logWarn('chunk_load_failed', {
    errorId,
    message: error && error.message,
    stack: error && error.stack,
    component: source,
  });
  try {
    if (onChunkError) onChunkError({ errorId, error });
  } catch (e) {
    /* the prompt itself must never throw */
  }
  return errorId;
};

const onWindowError = (event) => {
  const error = event && (event.error || event);
  if (isChunkLoadError(error)) {
    handleChunk(error, 'window.onerror');
    return;
  }
  logError('window_error', {
    errorId: newErrorId('win'),
    message: (error && error.message) || (event && event.message),
    stack: error && error.stack,
    component: (event && event.filename) || null,
  });
  // Deliberately NOT calling preventDefault: swallowing the event would
  // hide the failure from any other tooling attached to the page. The app
  // stays alive either way — an uncaught error here does not unmount React.
};

const onRejection = (event) => {
  const reason = event && event.reason;
  if (isChunkLoadError(reason)) {
    handleChunk(reason, 'unhandledrejection');
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    return;
  }

  // Axios rejections are already handled by the axios interceptor and by
  // each call site's catch; logging them again at error level would flood
  // the sink with duplicates of things that were handled correctly.
  const isHandledHttp = !!(reason && reason.isAxiosError);

  const detail = {
    errorId: newErrorId('rej'),
    message: (reason && (reason.message || reason.toString())) || 'Unhandled promise rejection',
    stack: reason && reason.stack,
    api: (reason && reason.config && reason.config.url) || null,
    status: (reason && reason.response && reason.response.status) || null,
  };

  if (isHandledHttp) logWarn('unhandled_rejection_http', detail);
  else logError('unhandled_rejection', detail);
};

// A failed <script>/<img>/<link> fires a non-bubbling 'error' on the element
// itself, so it only reaches us in the capture phase. A failed script chunk
// shows up here rather than as a window.onerror.
const onResourceError = (event) => {
  const target = event && event.target;
  if (!target || target === window) return;
  const tag = (target.tagName || '').toLowerCase();
  if (tag !== 'script' && tag !== 'link' && tag !== 'img') return;
  const src = target.src || target.href || null;
  if (tag === 'script' && src && /\/static\/js\/.*\.chunk\.js/.test(src)) {
    handleChunk(new Error('Loading chunk failed: ' + src), 'resource');
    return;
  }
  logWarn('resource_load_failed', { errorId: newErrorId('res'), message: tag + ' failed to load', api: src });
};

/**
 * Install once, as early as possible (index.js, before React renders).
 * Safe to call repeatedly — subsequent calls are no-ops.
 */
export const installGlobalErrorHandlers = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  try {
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onResourceError, true);
  } catch (e) {
    installed = false;
  }
};

export const uninstallGlobalErrorHandlers = () => {
  if (!installed || typeof window === 'undefined') return;
  try {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('error', onResourceError, true);
  } catch (e) {
    /* no-op */
  }
  installed = false;
};

export default { installGlobalErrorHandlers, uninstallGlobalErrorHandlers, isChunkLoadError, setChunkErrorHandler };
